/**
 * Custom Evaluator Sets API — create and manage custom rubric eval sets.
 *
 * Endpoints used:
 *   POST /api/evals/custom-evaluator-sets
 *     → Creates a new eval set with an optional initial draft version.
 *
 *   POST /api/evals/custom-evaluator-sets/{setId}/versions/{versionId}/publish
 *     → Promotes a draft version to active status.
 */
import apiClient, { type ApiResponse } from './client';

// ─── Request types ────────────────────────────────────────────────────────────

export interface CustomEvalCriterionCreate {
  /** Stable, URL-safe identifier for the criterion (max 128 chars). */
  key: string;
  /** Human-readable display name (max 255 chars). */
  name: string;
  /** The evaluation rubric / LLM scoring instructions. */
  rubric: string;
  score_type?: string;
  pass_threshold?: number | null;
  weight?: number;
  order_index?: number;
  is_required?: boolean;
}

export interface CustomEvalSetVersionCreate {
  notes?: string;
  model_name?: string;
  temperature?: number;
  criteria: CustomEvalCriterionCreate[];
}

export interface CreateCustomEvalSetRequest {
  organization_id: string;
  name: string;
  description?: string;
  user_id?: string;
  initial_version?: CustomEvalSetVersionCreate;
}

export interface PublishVersionRequest {
  organization_id: string;
  user_id?: string;
}

// ─── Response types ───────────────────────────────────────────────────────────

export interface CustomEvalCriterionResponse {
  id: string;
  set_version_id: string;
  key: string;
  name: string;
  rubric: string;
  score_type: string;
  pass_threshold: number | null;
  weight: number;
  order_index: number;
  is_required: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomEvalSetVersionResponse {
  id: string;
  set_id: string;
  version_number: number;
  /** "draft" | "active" | "archived" */
  status: string;
  model_name: string;
  temperature: number;
  notes: string | null;
  published_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  criteria: CustomEvalCriterionResponse[];
}

export interface CustomEvalSetResponse {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  active_version_id: string | null;
  created_at: string;
  updated_at: string;
  active_version: CustomEvalSetVersionResponse | null;
  versions: CustomEvalSetVersionResponse[];
}

// ─── Service ──────────────────────────────────────────────────────────────────

const customEvalsService = {
  /**
   * Create a new custom eval set.
   * Pass `initial_version` to also create a draft version with criteria in one call.
   */
  createEvalSet: (
    data: CreateCustomEvalSetRequest,
  ): Promise<ApiResponse<CustomEvalSetResponse>> =>
    apiClient.post<CustomEvalSetResponse>('/api/evals/custom-evaluator-sets', data),

  /**
   * Publish a draft version, making it the active version for the eval set.
   */
  publishVersion: (
    setId: string,
    versionId: string,
    data: PublishVersionRequest,
  ): Promise<ApiResponse<CustomEvalSetResponse>> =>
    apiClient.post<CustomEvalSetResponse>(
      `/api/evals/custom-evaluator-sets/${setId}/versions/${versionId}/publish`,
      data,
    ),
};

export default customEvalsService;
